import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coins, Mail, Shield, ChartLine, ArrowLeft, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/ui/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LoginPage = () => {
  const { isAuthenticated, isLoading, setUser } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('email'); // 'email', 'code', 'success'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes('@')) {
      setError(t('invalidEmail') || 'Please enter a valid email address');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep('code');
        setCountdown(60); // 60 second cooldown
        setCode(['', '', '', '', '', '']);
      } else {
        setError(data.detail || t('failedToSendCode') || 'Failed to send code. Please try again.');
      }
    } catch (err) {
      console.error('Send code error:', err);
      setError(t('somethingWentWrong') || 'Something went wrong. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerifyCode(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleVerifyCode(pastedData);
    }
  };

  const handleVerifyCode = async (codeString) => {
    const fullCode = codeString || code.join('');
    if (fullCode.length !== 6) {
      setError(t('enterFullCode') || 'Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          email: email.toLowerCase().trim(),
          code: fullCode 
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep('success');
        setUser(data.user);
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } else {
        setError(data.detail || t('invalidCode') || 'Invalid code. Please try again.');
        // Clear code on error
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('Verify code error:', err);
      setError(t('somethingWentWrong') || 'Something went wrong. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const features = [
    { icon: Coins, text: t('trackYourGoldHoldings') || 'Track your gold holdings' },
    { icon: ChartLine, text: t('monitorDailyPerformance') || 'Monitor daily performance' },
    { icon: Shield, text: t('secureAndPrivate') || 'Secure & private' }
  ];

  // Success screen
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-[#12121a] border border-[#1e1e2e] rounded-2xl p-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-500/10 flex items-center justify-center"
            >
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </motion.div>
            
            <h2 className="text-2xl font-bold text-white mb-2">{t('welcomeBack') || 'Welcome!'}</h2>
            <p className="text-gray-400">
              {t('redirectingToDashboard') || 'Redirecting to your dashboard...'}
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Code verification screen
  if (step === 'code') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
              <Coins className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-bold text-white">
              BuyMe<span className="text-[#D4AF37]">Gold</span>
            </span>
          </div>

          <div className="bg-[#12121a] border border-[#1e1e2e] rounded-2xl p-8">
            <button
              onClick={() => {
                setStep('email');
                setCode(['', '', '', '', '', '']);
                setError(null);
              }}
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('back') || 'Back'}
            </button>

            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('enterVerificationCode') || 'Enter verification code'}
              </h2>
              <p className="text-gray-400 text-sm">
                {t('codeSentTo') || 'We sent a 6-digit code to'}<br />
                <span className="text-white font-medium">{email}</span>
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}

            {/* 6-digit code input */}
            <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold bg-[#0a0a0f] border-2 border-[#1e1e2e] rounded-xl text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
                  autoFocus={index === 0}
                  disabled={isVerifying}
                />
              ))}
            </div>

            <Button
              onClick={() => handleVerifyCode()}
              disabled={isVerifying || code.join('').length !== 6}
              className="w-full h-12 btn-gold text-black font-semibold rounded-xl disabled:opacity-50"
            >
              {isVerifying ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                t('verifyAndSignIn') || 'Verify & Sign In'
              )}
            </Button>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500 mb-2">
                {t('didntReceiveCode') || "Didn't receive the code?"}
              </p>
              <Button
                onClick={handleSendCode}
                disabled={countdown > 0 || isSending}
                className="text-[#D4AF37] hover:text-[#F5ECD7] text-sm bg-transparent hover:bg-transparent disabled:opacity-50"
              >
                {isSending ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {countdown > 0 
                  ? `${t('resendIn') || 'Resend in'} ${countdown}s` 
                  : (t('resendCode') || 'Resend code')
                }
              </Button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <a href="/" className="text-gray-400 hover:text-white text-sm transition-colors">
              ← {t('backToHomepage') || 'Back to homepage'}
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  // Email input screen (default)
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0a0a0f] to-[#12121a] items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
              <Coins className="w-6 h-6 text-black" />
            </div>
            <span className="text-2xl font-bold text-white">
              BuyMe<span className="text-[#D4AF37]">Gold</span>
            </span>
          </div>

          <h1 className="text-4xl font-bold text-white mb-4">
            {t('trackYourGoldJourney') || 'Track Your'} <span className="text-gradient-gold">{t('gold') || 'Gold'}</span> {t('journey') || 'Journey'}
          </h1>
          
          <p className="text-lg text-gray-400 mb-8">
            {t('loginPageDescription') || 'Create an account to track your gold holdings. Watch how your investment performs over time with live market data.'}
          </p>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-3 text-gray-300"
              >
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span>{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
              <Coins className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-bold text-white">
              BuyMe<span className="text-[#D4AF37]">Gold</span>
            </span>
          </div>

          <div className="bg-[#12121a] border border-[#1e1e2e] rounded-2xl p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('welcome') || 'Welcome'}</h2>
                <p className="text-gray-400">{t('signInWithEmail') || 'Sign in with your email'}</p>
              </div>
              <LanguageSelector variant="minimal" />
            </div>

            <form onSubmit={handleSendCode}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-gray-300">{t('emailAddress') || 'Email address'}</Label>
                  <div className="relative mt-2">
                    <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500`} />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      className={`h-12 ${isRTL ? 'pr-11' : 'pl-11'} bg-[#0a0a0f] border-[#1e1e2e] text-white placeholder:text-gray-500 focus:border-[#D4AF37]`}
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-400">{error}</p>
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={isSending || !email}
                  className="w-full h-12 btn-gold text-black font-semibold rounded-xl disabled:opacity-50"
                >
                  {isSending ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      {t('sendVerificationCode') || 'Send Verification Code'}
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  {t('verificationCodeDescription') || "We'll send you a 6-digit code to sign in. No password needed!"}
                </p>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                {t('bySigningIn') || 'By signing in, you agree to our'}{' '}
                <a href="/terms" className="text-[#D4AF37] hover:underline">{t('termsOfService') || 'Terms of Service'}</a>
                {' '}{t('and') || 'and'}{' '}
                <a href="/privacy" className="text-[#D4AF37] hover:underline">{t('privacyPolicy') || 'Privacy Policy'}</a>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <a href="/" className="text-gray-400 hover:text-white text-sm transition-colors">
              ← {t('backToHomepage') || 'Back to homepage'}
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
